import {
  ConstructionStandardOptions,
  CorrectionReasonOptions,
  DeliveryContextGMWOptions,
  ElectrodePackingMaterialOptions,
  ElectrodeStatusOptions,
  GlueOptions,
  GroundLevelPositioningMethodOptions,
  IndicationYesNoOptions,
  InitialFunctionOptions,
  OptionalIndicationYesNoOptions,
  PositioningMethodOptions,
  QualityRegimeOptions,
  RegistrationTypeOptions,
  ScreenProtectionOptions,
  SockMaterialOptions,
  TubeMaterialOptions,
  TubePackingMaterialOptions,
  TubeStatusOptions,
  TubeTopPositioningMethodOptions,
  TubeTypeOptions,
  VerticalDatumOptions,
  WellHeadProtectorOptions,
  WellStabilityOptions,
  constructionStandardOptions,
  correctionReasonOptions,
  deliveryContextGMWOptions,
  electrodePackingMaterialOptions,
  electrodeStatusOptions,
  glueOptions,
  groundLevelPositioningMethodOptions,
  indicationYesNoOptions,
  initialFunctionOptions,
  optionalIndicationYesNoOptions,
  positioningMethodOptions,
  qualityRegimeOptions,
  registrationTypeOptions,
  screenProtectionOptions,
  sockMaterialOptions,
  tubeMaterialOptions,
  tubePackingMaterialOptions,
  tubeStatusOptions,
  tubeTopPositioningMethodOptions,
  tubeTypeOptions,
  verticalDatumOptions,
  wellHeadProtectorOptions,
  wellStabilityOptions,
} from "@nens/brostar/uploadapi-client";

import { addSchema } from "@/helpers/schema-utils";

const GMW_FIELD_SCHEMA_LIBRARY = {
  // Event form fields
  registrationType: addSchema<RegistrationTypeOptions>({
    type: "string",
    title: "Gebeurtenis type",
    enum: registrationTypeOptions.map((opt) => opt.value),
  }),
  eventDate: addSchema<string>({ type: "string", title: "Gebeurtenis datum", format: "date" }),

  // Metadata fields
  projectNumber: addSchema<string>({ type: "string", title: "Projectnummer" }),
  broId: addSchema<string>({ type: "string", title: "BRO-ID Put" }),
  qualityRegime: addSchema<QualityRegimeOptions>({
    type: "string",
    title: "Kwaliteitsregime",
    enum: qualityRegimeOptions.map((opt) => opt.value),
  }),
  deliveryAccountableParty: addSchema<string>({ type: "string", title: "Bronhouder [KVK]" }),
  correctionReason: addSchema<CorrectionReasonOptions>({
    type: "string",
    title: "Correctie reden",
    enum: correctionReasonOptions.map((opt) => opt.value),
  }),

  // GMW Start Registration fields
  objectIdAccountableParty: addSchema<string>({ type: "string", title: "Interne naam" }),
  nitgCode: addSchema<string>({ type: "string", title: "NITG-code" }),
  deliveryContext: addSchema<DeliveryContextGMWOptions>({
    type: "string",
    title: "Kader aanlevering",
    enum: deliveryContextGMWOptions.map((opt) => opt.value),
  }),
  constructionStandard: addSchema<ConstructionStandardOptions>({
    type: "string",
    title: "Kwaliteitsnorm inrichting",
    enum: constructionStandardOptions.map((opt) => opt.value),
  }),
  initialFunction: addSchema<InitialFunctionOptions>({
    type: "string",
    title: "Initiële functie",
    enum: initialFunctionOptions.map((opt) => opt.value),
    default: "onbekend",
  }),
  numberOfMonitoringTubes: addSchema<number>({
    type: "number",
    title: "Aantal monitoringsbuizen",
    minimum: 0,
    maximum: 50,
  }),
  wellConstructionDate: addSchema<string>({
    type: "string",
    title: "Inrichtingsdatum put",
    format: "date",
  }),
  deliveredLocation: addSchema<string>({ type: "string", title: "Aangeleverde locatie (x y)" }),
  horizontalPositioningMethod: addSchema<PositioningMethodOptions>({
    type: "string",
    title: "Methode locatiebepaling",
    enum: positioningMethodOptions.map((opt) => opt.value),
  }),
  localVerticalReferencePoint: addSchema<VerticalDatumOptions>({
    type: "string",
    title: "Lokaal verticaal referentiepunt",
    enum: verticalDatumOptions.map((opt) => opt.value),
  }),
  offset: addSchema<string>({ type: "string", title: "Verschuiving (m)" }),
  verticalDatum: addSchema<VerticalDatumOptions>({
    type: "string",
    title: "Verticaal datum",
    enum: verticalDatumOptions.map((opt) => opt.value),
  }),

  // Optional
  isAbroad: addSchema<OptionalIndicationYesNoOptions>({
    type: "string",
    title: "In het buitenland gelegen",
    enum: optionalIndicationYesNoOptions.map((opt) => opt.value),
  }),
  geometricDataPubliclyAvailable: addSchema<OptionalIndicationYesNoOptions>({
    type: "string",
    title: "Geometrische gegevens openbaar beschikbaar",
    enum: optionalIndicationYesNoOptions.map((opt) => opt.value),
  }),
  // Array of surveys (str with BHR000002003257 or CPT000000002257)
  relatedSurveys: addSchema<string[]>({
    type: "array",
    title: "Gerelateerde verkenningen",
    items: {
      type: "string",
      pattern: "^(BHR|CPT)\\d{12}$",
    },
  }),

  // Ground level fields
  wellStability: addSchema<WellStabilityOptions>({
    type: "string",
    title: "Put stabiliteit",
    enum: wellStabilityOptions.map((opt) => opt.value),
  }),
  groundLevelStable: addSchema<IndicationYesNoOptions>({
    type: "string",
    title: "Maaiveld stabiel",
    enum: indicationYesNoOptions.map((opt) => opt.value),
    default: "nee",
  }),
  groundLevelPosition: addSchema<string>({ type: "string", title: "Maaiveldpositie (mNAP)" }),
  groundLevelPositioningMethod: addSchema<GroundLevelPositioningMethodOptions>({
    type: "string",
    title: "Methode positiebepaling maaiveld",
    enum: groundLevelPositioningMethodOptions.map((opt) => opt.value),
  }),

  // Insertion fields
  insertedPartLength: addSchema<number>({
    type: "number",
    title: "Ingebracht deel lengte",
    minimum: 0.01,
  }),
  insertedPartDiameter: addSchema<number>({
    type: "number",
    title: "Ingebracht deel diameter",
    minimum: 1,
  }),
  insertedPartMaterial: addSchema<TubeMaterialOptions>({
    type: "string",
    title: "Ingebracht deel materiaal",
    enum: tubeMaterialOptions.map((opt) => opt.value),
  }),

  // Monitoring tube fields
  tubeNumber: addSchema<string>({ type: "string", title: "Buis nummer" }),
  tubeType: addSchema<TubeTypeOptions>({
    type: "string",
    title: "Buis type",
    enum: tubeTypeOptions.map((opt) => opt.value),
  }),
  artesianWellCapPresent: addSchema<IndicationYesNoOptions>({
    type: "string",
    title: "Artesische putdop aanwezig",
    enum: indicationYesNoOptions.map((opt) => opt.value),
  }),
  sedimentSumpPresent: addSchema<IndicationYesNoOptions>({
    type: "string",
    title: "Zandvang aanwezig",
    enum: indicationYesNoOptions.map((opt) => opt.value),
  }),
  numberOfGeoOhmCables: addSchema<number>({
    type: "number",
    title: "Aantal geo-ohm kabels",
    minimum: 0,
    maximum: 10,
  }),
  tubeTopDiameter: addSchema<number>({
    type: "number",
    title: "Bovenkant buis diameter (mm)",
    minimum: 3,
    maximum: 1000,
  }),
  variableDiameter: addSchema<IndicationYesNoOptions>({
    type: "string",
    title: "Variabele diameter",
    enum: indicationYesNoOptions.map((opt) => opt.value),
  }),
  tubeStatus: addSchema<TubeStatusOptions>({
    type: "string",
    title: "Buis status",
    enum: tubeStatusOptions.map((opt) => opt.value),
  }),
  tubeTopPosition: addSchema<number>({
    type: "number",
    title: "Bovenkant buis positie (mNAP)",
    minimum: -10,
    maximum: 325,
  }),
  tubeTopPositioningMethod: addSchema<TubeTopPositioningMethodOptions>({
    type: "string",
    title: "Bovenkant buis positioneringsmethode",
    enum: tubeTopPositioningMethodOptions.map((opt) => opt.value),
  }),
  tubePackingMaterial: addSchema<TubePackingMaterialOptions>({
    type: "string",
    title: "Buis opvulmateriaal",
    enum: tubePackingMaterialOptions.map((opt) => opt.value),
  }),
  tubeMaterial: addSchema<TubeMaterialOptions>({
    type: "string",
    title: "Buis materiaal",
    enum: tubeMaterialOptions.map((opt) => opt.value),
  }),
  glue: addSchema<GlueOptions>({
    type: "string",
    title: "Lijm",
    enum: glueOptions.map((opt) => opt.value),
  }),
  screenLength: addSchema<number>({
    type: "number",
    title: "Filter lengte (m)",
    minimum: 0,
    maximum: 100,
  }),
  sockMaterial: addSchema<SockMaterialOptions>({
    type: "string",
    title: "Kous materiaal",
    enum: sockMaterialOptions.map((opt) => opt.value),
  }),
  plainTubePartLength: addSchema<number>({
    type: "number",
    title: "Buis lengte (m)",
    minimum: 0,
  }),
  sedimentSumpLength: addSchema<number>({
    type: "number",
    title: "Zandvang lengte (m)",
  }),
  screenProtection: addSchema<ScreenProtectionOptions>({
    type: "string",
    title: "Filter bescherming",
    enum: screenProtectionOptions.map((opt) => opt.value),
  }),

  // Party fields
  wellHeadProtector: addSchema<WellHeadProtectorOptions>({
    type: "string",
    title: "Beschermconstructie",
    enum: wellHeadProtectorOptions.map((opt) => opt.value),
  }),
  maintenanceResponsibleParty: addSchema<string>({
    type: "string",
    title: "Onderhoudende instantie [KVK]",
  }),
  owner: addSchema<string>({ type: "string", title: "Eigenaar [KVK]" }),

  // Electrode fields
  electrodeNumber: addSchema<string>({ type: "string", title: "Elektrode nummer" }),
  electrodePackingMaterial: addSchema<ElectrodePackingMaterialOptions>({
    type: "string",
    title: "Elektrode opvulmateriaal",
    enum: electrodePackingMaterialOptions.map((opt) => opt.value),
  }),
  electrodeStatus: addSchema<ElectrodeStatusOptions>({
    type: "string",
    title: "Elektrode status",
    enum: electrodeStatusOptions.map((opt) => opt.value),
  }),
  electrodePosition: addSchema<number>({
    type: "number",
    title: "Elektrode positie (mNAP)",
    minimum: -750,
    maximum: 50,
  }),

  // Geo-ohm cable fields
  cableNumber: addSchema<string>({ type: "string", title: "Kabel nummer" }),
};

type FieldSchemaLibraryType = typeof GMW_FIELD_SCHEMA_LIBRARY;

export function pickGmwLibraryFields<K extends keyof FieldSchemaLibraryType>(
  fieldNames: readonly K[]
): Pick<FieldSchemaLibraryType, K> {
  const result = {} as Pick<FieldSchemaLibraryType, K>;
  for (const name of fieldNames) {
    result[name] = GMW_FIELD_SCHEMA_LIBRARY[name];
  }
  return result;
}
